import { useCallback } from 'react';
import { getIn } from 'formik';
import { Grid, Stack } from '@mui/material';

import { FBoxContainer } from '../../../../../../../components/baseV2';
import { QuestionnaireSectionLabel } from '../../../../../common';
import { LIST_CONTAINER_SX } from '../../../../../../common';
import { FORTA_GRAY1 } from '../../../../../../../utils/colors';
import ReferralListField from './ReferralListField';
import { REFERRAL_DIAGNOSIS_OPTIONS, REFERRAL_DIAGNOSTIC_ASSESSMENT_OPTIONS } from '../constants';

import type { AssessmentsDiagnosesSectionProps, ReferralFieldError } from './types';

function isStringError(value: ReferralFieldError): value is string {
  return Object.prototype.toString.call(value) === '[object String]';
}

export default function AssessmentsDiagnosesSection({
  formik,
  disabled,
  saveFieldOnBlur,
}: AssessmentsDiagnosesSectionProps) {
  const assessmentsError = getIn(formik.errors, 'diagnostic_assessments_used');
  const diagnosesError = getIn(formik.errors, 'diagnoses');
  const showAssessmentsError =
    !disabled &&
    Boolean(getIn(formik.touched, 'diagnostic_assessments_used')) &&
    isStringError(assessmentsError);
  const showDiagnosesError =
    !disabled && Boolean(getIn(formik.touched, 'diagnoses')) && isStringError(diagnosesError);

  const handleAssessmentsChange = useCallback(
    (next: string[]) => {
      formik.setFieldValue('diagnostic_assessments_used', next);
      // false: avoid revalidating stale pre-update values over setFieldValue's result
      formik.setFieldTouched('diagnostic_assessments_used', true, false);
      void saveFieldOnBlur('diagnostic_assessments_used', next);
    },
    [formik, saveFieldOnBlur],
  );

  const handleDiagnosesChange = useCallback(
    (next: string[]) => {
      formik.setFieldValue('diagnoses', next);
      // false: avoid revalidating stale pre-update values over setFieldValue's result
      formik.setFieldTouched('diagnoses', true, false);
      void saveFieldOnBlur('diagnoses', next);
    },
    [formik, saveFieldOnBlur],
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Stack gap={1}>
          <QuestionnaireSectionLabel variant="h6" fontWeight={700} sx={{ mb: { xs: 2, md: 2 } }}>
            Diagnosis assessment(s) used
          </QuestionnaireSectionLabel>
          <FBoxContainer sx={LIST_CONTAINER_SX}>
            <ReferralListField
              addButtonLabel="Add assessment"
              confirmDeleteTitle="Confirm assessment deletion?"
              disabled={disabled}
              error={showAssessmentsError}
              helperText={showAssessmentsError ? assessmentsError : undefined}
              label="Diagnosis assessment(s) used"
              onChange={handleAssessmentsChange}
              options={REFERRAL_DIAGNOSTIC_ASSESSMENT_OPTIONS}
              placeholder="Select assessments used"
              required
              requiredIndicatorSx={{ bgcolor: FORTA_GRAY1 }}
              values={formik.values.diagnostic_assessments_used}
            />
          </FBoxContainer>
        </Stack>
      </Grid>
      <Grid item xs={12} md={6}>
        <Stack gap={1}>
          <QuestionnaireSectionLabel variant="h6" fontWeight={700} sx={{ mb: { xs: 1, md: 2 } }}>
            Diagnosis
          </QuestionnaireSectionLabel>
          <FBoxContainer sx={LIST_CONTAINER_SX}>
            <ReferralListField
              addButtonLabel="Add diagnosis"
              confirmDeleteTitle="Confirm diagnosis deletion?"
              disabled={disabled}
              error={showDiagnosesError}
              helperText={showDiagnosesError ? diagnosesError : undefined}
              label="Diagnosis"
              onChange={handleDiagnosesChange}
              options={REFERRAL_DIAGNOSIS_OPTIONS}
              placeholder="Select diagnosis"
              required
              requiredIndicatorSx={{ bgcolor: FORTA_GRAY1 }}
              values={formik.values.diagnoses}
            />
          </FBoxContainer>
        </Stack>
      </Grid>
    </Grid>
  );
}
